import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Button, Space} from 'antd';
import {StyledBalanceDetailsWarning, StyledBalancePageHeaderVisibility} from './StyledBalancePageHeader';
import AddFundsDetails from '../../SubscriptionsPage/AddFundsDetails';
import PageHeader from '../../../Header/PageHeader';
import BalanceLoading from '../BalanceLoading';
import {WarningIcon} from '../../../../icons';
import {elementHelpers, transactionsHelpers} from '../../../../utils/helpers';
import {localStorageKeysConstants} from '../../../../constants';
import {useCurrentCompany, useIsAdmin, useIsBankingEnabled, useIsPsd2ObAvailable, useWireDetails} from '../../../../hooks';
import AccountBalanceDropdown from '../AccountBalanceDropdown';
import BalanceDetails from '../BalanceDetails';

const BalancePageHeader = () => {
  const [t] = useTranslation('main');
  // Starts true so BalanceLoading auto-fires its initial fetch on session start,
  // matching the deleted App.js-level `<BalanceLoading />` (which had no props, so
  // BalanceLoading.jsx's own `enabledLoading = true` default did the same job).
  // handleRetry() below re-arms it after a completed load resets it to false.
  const [enabledLoading, setEnabledLoading] = useState(true);

  const breadcrumbs = useSelector((state) => state.main.breadcrumbs);
  const balanceDetails = useSelector((state) => state.transaction?.balanceDetails ?? {});
  const isAdmin = useIsAdmin();
  const wireDetails = useWireDetails();
  const {id: companyId} = useCurrentCompany();

  const cName = elementHelpers.getClassName('balance-page-header');

  const handleRetry = () => {
    localStorage.removeItem(localStorageKeysConstants.CLOSED_BALANCE_AUTH);
    setEnabledLoading(true);
  }

  const isBankingEnabled = useIsBankingEnabled();
  const isPsd2ObAvailable = useIsPsd2ObAvailable();

  // balanceLoadingCallback below latches enabledLoading to false once a load settles
  // (success or failure) so BalanceLoading doesn't loop; nothing re-arms it afterwards
  // except the manual Retry button. A company switch needs the same fresh attempt a
  // Retry click gets, or the new company's balance/SCA prompt never appears (H1MVP-5024
  // follow-up: banking.isEnabledBanking now correctly toggles on switch, but this latch
  // was still swallowing the resulting BalanceLoading re-run).
  useEffect(() => {
    setEnabledLoading(true);
  }, [companyId]);

  const getHeaderButtons = () => {
    let buttons = [];
    if (isAdmin) {
      if (isPsd2ObAvailable) {
        buttons = [
          ...buttons,
          <AccountBalanceDropdown showTreezorBalance={isBankingEnabled} />
        ];
      } else if (isBankingEnabled && balanceDetails.isLoaded) {
        buttons = [
          ...buttons,
          <BalanceDetails name={t('balance')} value={transactionsHelpers.getBalanceDisplayValue(balanceDetails)} />,
          <AddFundsDetails details={wireDetails} />,
        ];
      } else if (isBankingEnabled && balanceDetails.isRequested) {
        buttons.push(
          <StyledBalanceDetailsWarning>
            <Space align='center' size='middle'>
              <Space align='center' size='small'>
                <WarningIcon />
                <span>{t('authenticateToSeeTheBalance')}</span>
              </Space>
              <Button
                className='primary-outline'
                onClick={handleRetry}
                loading={enabledLoading}
              >
                {t('retry')}?
              </Button>
            </Space>
          </StyledBalanceDetailsWarning>
        );
      }
    }
    return buttons;
  }

  const headerButtons = getHeaderButtons();

  const balanceLoadingCallback = () => setEnabledLoading(false);

  return (
    <>
      {/* Always mounted, never conditionally rendered — only visibility toggles.
          AddFundsDetails (inside headerButtons) owns an SCA challenge modal that must
          survive navigation; unmounting PageHeader here would unmount it too. */}
      <StyledBalancePageHeaderVisibility $visible={breadcrumbs?.length > 0}>
        <PageHeader
          buttons={headerButtons}
          className={cName}
          breadcrumbs={breadcrumbs}
        />
      </StyledBalancePageHeaderVisibility>


      {(isAdmin && isBankingEnabled) && (
        <BalanceLoading
          enabledLoading={enabledLoading}
          errorCallback={balanceLoadingCallback}
          successCallback={balanceLoadingCallback}
        />
      )}
    </>
  );
}

export default BalancePageHeader;
